
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import '../../Styles/ManagementPages.css';
import '../../Styles/CustomerManage.css';
import { 
  getBusOwners,
  getPendingBuses,
  approvePendingBus,
  rejectPendingBus,
  getPendingBusById,
  updatePendingBus  
} from '../../Services/Operations/ProductAPI';
import LiveSeatStatusModal from './LiveSeatStatusModal';
import BusActivityLogModal from './BusActivityLogModal';
import { toast } from 'react-toastify';
import EditBusModal from './EditBusModal';

const SkeletonStatsCard = () => (
  <div className="stat-card skeleton">
    <div className="skeleton-title"></div>
    <div className="skeleton-value"></div>
    <div className="skeleton-subtitle"></div>
  </div>
);

const SkeletonTableRow = () => (
  <tr className="skeleton-row">
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-badge"></div></td>
    <td><div className="skeleton-actions"></div></td>
  </tr>
);

const SkeletonTable = () => (
  <div className="table-container skeleton">
    <table className="data-table">
      <thead>
        <tr>
          <th>No.</th>
          <th>Bus Number</th>
          <th>Bus Name</th>
          <th>Bus Type</th>
          <th>Category</th>
          <th>Total Seats</th>
          <th>Available Seats</th>
          <th>Owner Name</th>
          <th>Owner Phone</th>
          <th>Logs</th>
          <th>Status</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
          <SkeletonTableRow key={i} />
        ))}
      </tbody>
    </table>
  </div>
);

const BusManagement = () => {
  const { token } = useSelector(state => state.user);
  const [error, setError] = useState(null);
  const [allBusesData, setAllBusesData] = useState([]);
  const [filteredBuses, setFilteredBuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  const [viewMode, setViewMode] = useState('regular');
  const [pendingBuses, setPendingBuses] = useState([]);
  const [loadingPending, setLoadingPending] = useState(false);
  
  const [viewingBus, setViewingBus] = useState(null);
  const [seatStatusBus, setSeatStatusBus] = useState(null);
  const [logModal, setLogModal] = useState({ show: false, busId: null, busName: '', busNumber: '' });

  const [currentPage, setCurrentPage] = useState(1);
  const [busesPerPage] = useState(10);
  const [totalBusesCount, setTotalBusesCount] = useState(0);
  const [totalOwnersCount, setTotalOwnersCount] = useState(0);

const [showEditModal, setShowEditModal] = useState(false);
const [editingPendingBus, setEditingPendingBus] = useState(null);
const [isEditingPending, setIsEditingPending] = useState(false);

const handleEditPendingBus = async (bus) => {
  try {
    setError(null);
    
    const response = await getPendingBusById(bus.id, token);
    
    if (response && response.success) {
      const busData = response.data;
      setEditingPendingBus({
        ...busData,
        id: busData._id,
        isPending: true
      });
      setIsEditingPending(true);
      setShowEditModal(true);
    } else {
      toast.error('Failed to fetch pending bus details');
    }
  } catch (err) {
    console.error('Error fetching pending bus details:', err);
    toast.error('Failed to fetch pending bus details');
  }
};

const handleUpdatePendingBus = async (updatedData) => {
  try {
    setError(null);
    
    const response = await updatePendingBus(editingPendingBus._id, updatedData, token);
    
    if (response && response.success) {
      toast.success('Pending bus updated successfully!');
      
      await fetchPendingBuses();
      
      setShowEditModal(false);
      setEditingPendingBus(null);
      setIsEditingPending(false);
    } else {
      throw new Error(response?.message || 'Failed to update pending bus');
    }
  } catch (err) {
    console.error('Error updating pending bus:', err);
    toast.error(err.message || 'Failed to update pending bus');
    setError(err.message || 'Failed to update pending bus');
  }
};

  const fetchAllBusOwners = async () => {
    try {
      setLoading(true);
      let allOwners = [];
      let currentPageNum = 1;
      let totalPages = 1;

      do {
        const response = await getBusOwners(token, currentPageNum, 10, '');
        if (response && response.success && response.busOwners) {
          allOwners = [...allOwners, ...response.busOwners];
          totalPages = response.totalPages;
          setTotalOwnersCount(response.total);
          currentPageNum++;
        } else {
          break;
        }
      } while (currentPageNum <= totalPages);

      const buses = [];
      allOwners.forEach(owner => {
        if (owner.busAll && owner.busAll.length > 0) {
          owner.busAll.forEach(bus => {
            buses.push({
              ...bus,
              id: bus._id,
              busNumber: bus.busNumber,
              busName: bus.busName,
              busType: bus.busType,
              busCategory: bus.busCategory,
              totalSeats: bus.totalSeats,
              availableSeats: bus.availableSeats,
              amenities: bus.amenities || [],
              operatingDays: bus.operatingDays || [],
              isActive: bus.isActive,
              driver: bus.driver,
              conductor: bus.conductor,
              seats: bus.seats || [],
              routeSchedules: bus.routeSchedules || [],
              seatConfiguration: bus.seatConfiguration || {},
              ownerId: owner._id,
              ownerName: owner.name,
              ownerPhone: owner.phone,
              ownerEmail: owner.email,
              ownerCompany: owner.companyName,
              ownerAddress: owner.address,
              ownerCommissionRate: owner.commissionRate,
              ownerCommissionType: owner.commissionType,
              ownerIsVerified: owner.isVerified,
              ownerIsApproved: owner.isApprovedByAdmin,
              isPending: false,
              source: 'regular'
            });
          });
        }
      });

      setAllBusesData(buses);
      setFilteredBuses(buses);
      setTotalBusesCount(buses.length);

    } catch (err) {
      console.error('Error fetching bus owners:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingBuses = async () => {
    try {
      setLoadingPending(true);
      setError(null);

      const response = await getPendingBuses(token);

      if (response && response.success) {
        const transformedBuses = response.data.map((bus, index) => ({
          ...bus,
          id: bus._id,
          pendingId: bus._id,
          serialNo: index + 1,
          busNumber: bus.busNumber,
          busName: bus.busName,
          busType: bus.busType,
          busCategory: bus.busCategory,
          totalSeats: bus.totalSeats,
          availableSeats: bus.availableSeats || bus.totalSeats,
          amenities: bus.amenities || [],
          operatingDays: bus.operatingDays || [],
          isActive: false,
          driver: null,
          conductor: null,
          seats: bus.seats || [],
          routeSchedules: bus.routeSchedules || [],
          seatConfiguration: bus.seatConfiguration || {},
          ownerId: bus.owner,
          ownerName: bus.owner?.name || 'N/A',
          ownerPhone: bus.owner?.phone || 'N/A',
          ownerEmail: bus.owner?.email || 'N/A',
          ownerCompany: bus.owner?.companyName || 'N/A',
          isPending: true,
          source: 'pending',
          submittedBy: bus.submittedBy,
          submittedByName: bus.submittedByName,
          submittedByEmail: bus.submittedByEmail,
          operationType: bus.operationType,
          approvalStatus: bus.approvalStatus,
          originalData: bus.originalData
        }));

        setPendingBuses(transformedBuses);
        setTotalBusesCount(transformedBuses.length);
      } else {
        setPendingBuses([]);
        setTotalBusesCount(0);
      }
    } catch (err) {
      console.error('Error fetching pending buses:', err);
      setError('Failed to fetch pending buses. Please try again.');
      setPendingBuses([]);
    } finally {
      setLoadingPending(false);
    }
  };

  useEffect(() => {
    if (viewMode === 'regular') {
      fetchAllBusOwners();
    } else {
      fetchPendingBuses();
    }
  }, [viewMode]);

  useEffect(() => {
    const currentData = viewMode === 'regular' ? allBusesData : pendingBuses;
    
    if (searchTerm.trim() === '') {
      setFilteredBuses(currentData);
    } else {
      const searchLower = searchTerm.toLowerCase();
      const filtered = currentData.filter(bus =>
        bus.busNumber?.toLowerCase().includes(searchLower) ||
        bus.busName?.toLowerCase().includes(searchLower) ||
        bus.ownerName?.toLowerCase().includes(searchLower) ||
        bus.ownerPhone?.includes(searchLower)
      );
      setFilteredBuses(filtered);
    }
    setCurrentPage(1);
  }, [searchTerm, allBusesData, pendingBuses, viewMode]);

  const handleApprovePendingBus = async (pendingId) => {
    try {
      setError(null);
      const response = await approvePendingBus(pendingId, token);

      if (response.success) {
        toast.success('Pending bus approved successfully!');
        setPendingBuses(prev => prev.filter(bus => bus.id !== pendingId));
        setTotalBusesCount(prev => prev - 1);
        await fetchAllBusOwners();
      } else {
        throw new Error(response.message || 'Failed to approve pending bus');
      }
    } catch (err) {
      console.error('Error approving pending bus:', err);
      setError('Failed to approve pending bus. Please try again.');
      toast.error('Failed to approve pending bus. Please try again.');
    }
  };

  const handleRejectPendingBus = async (pendingId) => {
    if (!window.confirm('Are you sure you want to reject this pending bus?')) return;

    try {
      setError(null);
      const response = await rejectPendingBus(pendingId, token);

      if (response.success) {
        toast.success('Pending bus rejected successfully!');
        setPendingBuses(prev => prev.filter(bus => bus.id !== pendingId));
        setTotalBusesCount(prev => prev - 1);
      } else {
        throw new Error(response.message || 'Failed to reject pending bus');
      }
    } catch (err) {
      console.error('Error rejecting pending bus:', err);
      setError('Failed to reject pending bus. Please try again.');
      toast.error('Failed to reject pending bus. Please try again.');
    }
  };

const handleViewBus = async (bus) => {
  console.log("bus handleViewBus ?????????????? ",bus)
  try {
    setError(null);

    let response;
    let busData;
    
    if (bus.isPending) {
      response = await getPendingBusById(bus.id, token);
      if (response.success) {
        busData = response.data;
        
        let ownerName = 'N/A';
        let ownerPhone = 'N/A';
        let ownerEmail = 'N/A';
        let ownerCompany = 'N/A';
        let ownerAddress = null;
        
        if (busData.owner) {
          if (typeof busData.owner === 'object') {
            ownerName = busData.owner.name || 'N/A';
            ownerPhone = busData.owner.phone || 'N/A';
            ownerEmail = busData.owner.email || 'N/A';
            ownerCompany = busData.owner.companyName || 'N/A';
            ownerAddress = busData.owner.address || null;
          } else if (typeof busData.owner === 'string') {
            if (bus.ownerName && bus.ownerName !== 'N/A') {
              ownerName = bus.ownerName;
              ownerPhone = bus.ownerPhone || 'N/A';
              ownerEmail = bus.ownerEmail || 'N/A';
              ownerCompany = bus.ownerCompany || 'N/A';
            } else if (bus.owner) {
              if (typeof bus.owner === 'object') {
                ownerName = bus.owner.name || 'N/A';
                ownerPhone = bus.owner.phone || 'N/A';
                ownerEmail = bus.owner.email || 'N/A';
                ownerCompany = bus.owner.companyName || 'N/A';
                ownerAddress = bus.owner.address || null;
              }
            }
            else {
              if (busData.originalData && busData.originalData.owner) {
                const origOwner = busData.originalData.owner;
                if (typeof origOwner === 'object') {
                  ownerName = origOwner.name || 'N/A';
                  ownerPhone = origOwner.phone || 'N/A';
                  ownerEmail = origOwner.email || 'N/A';
                  ownerCompany = origOwner.companyName || 'N/A';
                  ownerAddress = origOwner.address || null;
                }
              }
            }
          }
        }
        
        if (bus.ownerName && bus.ownerName !== 'N/A' && ownerName === 'N/A') {
          ownerName = bus.ownerName;
          ownerPhone = bus.ownerPhone || 'N/A';
          ownerEmail = bus.ownerEmail || 'N/A';
          ownerCompany = bus.ownerCompany || 'N/A';
        }
        
        if (busData.originalData) {
          if (busData.originalData.ownerName && ownerName === 'N/A') {
            ownerName = busData.originalData.ownerName;
          }
          if (busData.originalData.ownerPhone && ownerPhone === 'N/A') {
            ownerPhone = busData.originalData.ownerPhone;
          }
          if (busData.originalData.ownerEmail && ownerEmail === 'N/A') {
            ownerEmail = busData.originalData.ownerEmail;
          }
          if (busData.originalData.ownerCompany && ownerCompany === 'N/A') {
            ownerCompany = busData.originalData.ownerCompany;
          }
        }
        
        if (bus.originalData && bus.originalData.owner) {
          const origOwner = bus.originalData.owner;
          if (typeof origOwner === 'object') {
            if (origOwner.name && ownerName === 'N/A') ownerName = origOwner.name;
            if (origOwner.phone && ownerPhone === 'N/A') ownerPhone = origOwner.phone;
            if (origOwner.email && ownerEmail === 'N/A') ownerEmail = origOwner.email;
            if (origOwner.companyName && ownerCompany === 'N/A') ownerCompany = origOwner.companyName;
          }
        }
        
        setViewingBus({
          ...busData,
          id: busData._id,
          busNumber: busData.busNumber,
          busName: busData.busName,
          busType: busData.busType,
          busCategory: busData.busCategory,
          totalSeats: busData.totalSeats,
          availableSeats: busData.availableSeats,
          amenities: busData.amenities || [],
          operatingDays: busData.operatingDays || [],
          isActive: false,
          driver: null,
          conductor: null,
          seats: busData.seats || [],
          routeSchedules: busData.routeSchedules || [],
          seatConfiguration: busData.seatConfiguration || {},
          ownerId: busData.owner?._id || busData.owner || bus.ownerId,
          ownerName: ownerName,
          ownerPhone: ownerPhone,
          ownerEmail: ownerEmail,
          ownerCompany: ownerCompany,
          ownerAddress: ownerAddress,
          isPending: true,
          submittedBy: busData.submittedBy,
          submittedByName: busData.submittedByName,
          submittedByEmail: busData.submittedByEmail,
          operationType: busData.operationType,
          originalData: busData.originalData
        });
      }
    } else {
      setViewingBus(bus);
    }
  } catch (err) {
    console.error('Error fetching bus details:', err);
    setError('Failed to fetch bus details.');
  } 
};

  const closeViewModal = () => {
    setViewingBus(null);
  };

  const handleOpenSeatStatus = (bus) => {
    console.log("Opening seat status for bus:", bus);
    console.log("Route schedules:", bus.routeSchedules);
    setSeatStatusBus(bus);
  };

  const closeSeatStatusModal = () => {
    setSeatStatusBus(null);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleRefresh = async () => {
    setSearchTerm('');
    setCurrentPage(1);
    if (viewMode === 'regular') {
      await fetchAllBusOwners();
    } else {
      await fetchPendingBuses();
    }
  };

  const handleExportCSV = () => {
    if (filteredBuses.length === 0) {
      alert('No data to export');
      return;
    }

    const headers = ['Serial No.', 'Bus Number', 'Bus Name', 'Bus Type', 'Category',
                     'Total Seats', 'Available Seats', 'Owner Name', 'Owner Phone',
                     'Owner Company', 'Status'];
    const csvData = filteredBuses.map((bus, index) => [
      index + 1,
      bus.busNumber || 'N/A',
      bus.busName || 'N/A',
      bus.busType || 'N/A',
      bus.busCategory || 'N/A',
      bus.totalSeats || 0,
      bus.availableSeats || 0,
      bus.ownerName || 'N/A',
      bus.ownerPhone || 'N/A',
      bus.ownerCompany || 'N/A',
      bus.isPending ? 'Pending' : (bus.isActive ? 'Active' : 'Inactive')
    ]);

    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.map(field => `"${field}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `buses-report-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    alert('CSV exported successfully!');
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const indexOfLastBus = currentPage * busesPerPage;
  const indexOfFirstBus = indexOfLastBus - busesPerPage;
  const currentBuses = filteredBuses.slice(indexOfFirstBus, indexOfLastBus);
  const totalPages = Math.ceil(filteredBuses.length / busesPerPage);

  const currentBusesWithSerial = currentBuses.map((bus, index) => ({
    ...bus,
    serialNo: indexOfFirstBus + index + 1
  }));

  const activeBuses = viewMode === 'regular' ? filteredBuses.filter(bus => bus.isActive).length : 0;
  const totalSeats = filteredBuses.reduce((sum, bus) => sum + (bus.totalSeats || 0), 0);
  const uniqueBusOwners = new Set(filteredBuses.map(bus => bus.ownerId)).size;

  const isLoading = viewMode === 'regular' ? loading : loadingPending;

  if (isLoading && filteredBuses.length === 0) {
    return (
      <div className="management-page">
        <div className="page-header">
          <div className="skeleton-header-title"></div>
          <div className="header-actions">
            <div className="skeleton-search"></div>
            <div className="skeleton-button"></div>
            <div className="skeleton-button"></div>
          </div>
        </div>

        <div className="stats-cards">
          {[1, 2, 3, 4].map((i) => (
            <SkeletonStatsCard key={i} />
          ))}
        </div>

        <SkeletonTable />

        <div className="pagination-container">
          <div className="skeleton-pagination-info"></div>
          <div className="skeleton-pagination-controls"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="management-page">
      <div className="page-header">
        <h1>Bus Management</h1>
        <div className="header-actions">
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              placeholder="Search by bus number, name, owner name or phone..."
              className="search-input"
              value={searchTerm}
              onChange={handleSearchChange}
              style={{ width: "300px" }}
            />
          </div>

        

          <button className="refresh-btn" onClick={handleRefresh}>
            Refresh
          </button>
          <button className="export-btn" onClick={handleExportCSV}>
            Export CSV
          </button>
        </div>
      </div>

      <div style={{ fontSize: '12px', color: '#666', marginBottom: '10px', textAlign: 'center' }}>
        {viewMode === 'regular' ? (
          <>Total Buses: {totalBusesCount} | Total Owners: {totalOwnersCount}</>
        ) : (
          <>Pending Buses: {pendingBuses.length} | {loadingPending ? 'Loading...' : ''}</>
        )}
      </div>

      <div className="stats-cards">
        <div className="stat-card">
          <h3>Total {viewMode === 'regular' ? 'Buses' : 'Pending Buses'}</h3>
          <p>{totalBusesCount}</p>
          <span>{viewMode === 'regular' ? 'All registered buses' : 'Awaiting approval'}</span>
        </div>
        <div className="stat-card">
          <h3>Active Buses</h3>
          <p>{activeBuses}</p>
          <span>Currently active</span>
        </div>
        <div className="stat-card">
          <h3>Total Seats</h3>
          <p>{totalSeats}</p>
          <span>Across all buses</span>
        </div>
        <div className="stat-card">
          <h3>Bus Owners</h3>
          <p>{uniqueBusOwners}</p>
          <span>Unique owners</span>
        </div>
      </div>


      <div className="view-toggle-group" style={{ display: 'flex', gap: '10px', alignItems: 'center', margin: '0 15px' }}>
        <button
          className={`toggle-btn ${viewMode === 'regular' ? 'active' : ''}`}
          onClick={() => {
            setViewMode('regular');
            setCurrentPage(1);
            setSearchTerm('');
          }}
          style={{
            padding: '8px 20px',
            borderRadius: '20px',
            border: '2px solid #007bff',
            backgroundColor: viewMode === 'regular' ? '#007bff' : 'transparent',
            color: viewMode === 'regular' ? 'white' : '#007bff',
            cursor: 'pointer',
            fontWeight: '500',
            transition: 'all 0.3s'
          }}
        >
          🚌 Regular
        </button>
        <button
          className={`toggle-btn ${viewMode === 'pending' ? 'active' : ''}`}
          onClick={() => {
            setViewMode('pending');
            setCurrentPage(1);
            setSearchTerm('');
          }}
          style={{
            padding: '8px 20px',
            borderRadius: '20px',
            border: '2px solid #ff9800',
            backgroundColor: viewMode === 'pending' ? '#ff9800' : 'transparent',
            color: viewMode === 'pending' ? 'white' : '#ff9800',
            cursor: 'pointer',
            fontWeight: '500',
            transition: 'all 0.3s',
            position: 'relative'
          }}
        >
          📋 Executive Submitted
          {viewMode === 'pending' && pendingBuses.length > 0 && (
            <span style={{
              position: 'absolute',
              top: '-8px',
              right: '-8px',
              backgroundColor: 'red',
              color: 'white',
              borderRadius: '50%',
              width: '20px',
              height: '20px',
              fontSize: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 'bold'
            }}>
              {pendingBuses.length}
            </span>
          )}
        </button>
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>No.</th>
              <th>Bus Number</th>
              <th>Bus Name</th>
              <th>Bus Type</th>
              <th>Category</th>
              <th>Total Seats</th>
              <th>Available Seats</th>
              <th>Owner Name</th>
              <th>Owner Phone</th>
              <th>Logs</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentBusesWithSerial.length > 0 ? (
              currentBusesWithSerial.map(bus => (
                <tr key={bus.id} className={bus.isPending ? 'pending-row' : ''}>
                  <td>{bus.serialNo}</td>
                  <td>
                    <span style={{ fontWeight: '600', color: bus.isPending ? '#ff9800' : '#0f6fbf' }}>
                      {bus.busNumber || 'N/A'}
                    </span>
                  </td>
                  <td>{bus.busName || 'N/A'}</td>
                  <td>
                    <span className={`bus-type-badge ${bus.busType === 'AC' ? 'ac' : 'non-ac'}`}>
                      {bus.busType || 'N/A'}
                    </span>
                  </td>
                  <td>
                    <span className="bus-category-badge">
                      {bus.busCategory || 'N/A'}
                    </span>
                  </td>
                  <td>{bus.totalSeats || 0}</td>
                  <td>
                    {bus.isPending ? (
                      <span style={{ color: '#ff9800' }}>Pending</span>
                    ) : (
                      <div className="available-seats-cell">
                        <button
                          className="action-btn live-seat-btn"
                          onClick={() => handleOpenSeatStatus(bus)}
                          title="View live seat status for a selected date"
                          style={{ backgroundColor: "#808080" }}
                        >
                          Live Seat Status
                        </button>
                      </div>
                    )}
                  </td>
                  <td>
                    <div>
                      <strong>{bus.ownerName || 'N/A'}</strong>
                      {bus.ownerCompany && (
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          {bus.ownerCompany}
                        </div>
                      )}
                    </div>
                  </td>
                  <td>{bus.ownerPhone || 'N/A'}</td>
                  <td>
                    {!bus.isPending && (
                      <div className="action-buttons">
                        <button
                          className="action-btn log-btn"
                          onClick={() => setLogModal({ 
                            show: true, 
                            busId: bus.id, 
                            busName: bus.busName,
                            busNumber: bus.busNumber
                          })}
                          style={{
                            backgroundColor: '#6f42c1',
                            color: 'white',
                            padding: '4px 12px',
                            borderRadius: '4px',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#5a32a3'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#6f42c1'}
                        >
                          Logs
                        </button>
                      </div>
                    )}
                  </td>
                  <td>
                    <span className={`status-badge ${bus.isPending ? 'pending' : (bus.isActive ? 'active' : 'inactive')}`}>
                      {bus.isPending ? 'Pending Approval' : (bus.isActive ? 'Active' : 'Inactive')}
                    </span>
                  </td>
                  <td>

                    {!bus.isPending ? (
                      <div className="action-buttons">
                        <button
                          className="action-btn view"
                          onClick={() => handleViewBus(bus)}
                        >
                          View
                        </button>
                      </div>
                    ) : (
                      <div className="action-buttons">
                        <button
                          className="action-btn view"
                          onClick={() => handleViewBus(bus)}
                          style={{ backgroundColor: '#17a2b8' }}
                        >
                          View
                        </button>
                        <button
                          className="action-btn edit"
                          onClick={() => handleEditPendingBus(bus)}
                          style={{ backgroundColor: '#6f42c1', color: 'white' }}
                        >
                          Edit
                        </button>
                        <button
                          className="action-btn approve"
                          onClick={() => handleApprovePendingBus(bus.id)}
                          style={{ backgroundColor: '#28a745' }}
                        >
                          Approve
                        </button>
                        <button
                          className="action-btn reject"
                          onClick={() => handleRejectPendingBus(bus.id)}
                          style={{ backgroundColor: '#dc3545' }}
                        >
                          Reject
                        </button>
                        {bus.submittedByName && (
                          <div style={{
                            fontSize: '10px',
                            color: '#666',
                            marginTop: '4px',
                            display: 'block'
                          }}>
                            By: {bus.submittedByName}
                          </div>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="12" className="no-data">
                  <p>
                    {viewMode === 'regular'
                      ? `No buses found ${searchTerm ? 'matching your search' : ''}.`
                      : 'No pending buses from executive submissions.'}
                  </p>
                  {searchTerm && (
                    <button className="clear-filters-link" onClick={handleRefresh}>
                      Clear search to see all buses
                    </button>
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {filteredBuses.length > 0 && (
        <div className="pagination-container">
          <div className="pagination-info">
            Showing {indexOfFirstBus + 1} to {Math.min(indexOfLastBus, filteredBuses.length)} of {filteredBuses.length} buses
            {searchTerm && <span className="filtered-indicator"> • Search: "{searchTerm}"</span>}
          </div>
          <div className="pagination-controls">
            <button
              className="pagination-btn"
              onClick={handlePrevPage}
              disabled={currentPage === 1}
            >
              Previous
            </button>

            <div className="page-numbers">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNumber;
                if (totalPages <= 5) {
                  pageNumber = i + 1;
                } else if (currentPage <= 3) {
                  pageNumber = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNumber = totalPages - 4 + i;
                } else {
                  pageNumber = currentPage - 2 + i;
                }

                return (
                  <button
                    key={pageNumber}
                    className={`page-number ${pageNumber === currentPage ? 'active' : ''}`}
                    onClick={() => handlePageClick(pageNumber)}
                  >
                    {pageNumber}
                  </button>
                );
              })}
            </div>

            <button
              className="pagination-btn"
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        </div>
      )}


      {viewingBus && (
        <div className="modal-overlay" onClick={closeViewModal}>
          <div className="modal-content1 view-route-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Bus Details: {viewingBus.busNumber || 'N/A'}</h3>
              <button className="modal-close-btn" onClick={closeViewModal}>
                ×
              </button>
            </div>

            <div className="modal-body view-modal-body">
              <div className="route-details">
                <div className="detail-section">
                  <h4>Bus Information</h4>
                  <div className="detail-row">
                    <label>Bus Number:</label>
                    <span style={{ fontWeight: 'bold', color: viewingBus.isPending ? '#ff9800' : '#007bff' }}>
                      {viewingBus.busNumber || 'N/A'}
                    </span>
                  </div>
                  <div className="detail-row">
                    <label>Bus Name:</label>
                    <span>{viewingBus.busName || 'N/A'}</span>
                  </div>
                  <div className="detail-row">
                    <label>Bus Type:</label>
                    <span className={`bus-type-badge ${viewingBus.busType === 'AC' ? 'ac' : 'non-ac'}`}>
                      {viewingBus.busType || 'N/A'}
                    </span>
                  </div>
                  <div className="detail-row">
                    <label>Bus Category:</label>
                    <span>{viewingBus.busCategory || 'N/A'}</span>
                  </div>
                  <div className="detail-row">
                    <label>Total Seats:</label>
                    <span>{viewingBus.totalSeats || 0}</span>
                  </div>
                  <div className="detail-row">
                    <label>Available Seats:</label>
                    <span>{viewingBus.availableSeats || 0}</span>
                  </div>
                  <div className="detail-row">
                    <label>Status:</label>
                    <span className={`status-badge ${viewingBus.isPending ? 'pending' : (viewingBus.isActive ? 'active' : 'inactive')}`}>
                      {viewingBus.isPending ? 'Pending Approval' : (viewingBus.isActive ? 'Active' : 'Inactive')}
                    </span>
                  </div>
                  {viewingBus.isPending && (
                    <>
                      <div className="detail-row">
                        <label>Submitted By:</label>
                        <span>{viewingBus.submittedByName || 'N/A'}</span>
                      </div>
                      <div className="detail-row">
                        <label>Submitted Email:</label>
                        <span>{viewingBus.submittedByEmail || 'N/A'}</span>
                      </div>
                      <div className="detail-row">
                        <label>Operation Type:</label>
                        <span>{viewingBus.operationType || 'N/A'}</span>
                      </div>
                    </>
                  )}
                </div>

                {viewingBus.amenities && viewingBus.amenities.length > 0 && (
                  <div className="detail-section">
                    <h4>Amenities</h4>
                    <div className="amenities-list">
                      {viewingBus.amenities.map((amenity, index) => (
                        <span key={index} className="amenity-badge">
                          {amenity}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {viewingBus.operatingDays && viewingBus.operatingDays.length > 0 && (
                  <div className="detail-section">
                    <h4>Operating Days</h4>
                    <div className="operating-days-list">
                      {viewingBus.operatingDays.map((day, index) => (
                        <span key={index} className="day-badge">
                          {day}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

               {!viewingBus.isPending && viewingBus.driver && (
  <div className="detail-section">
    <h4>Driver Information</h4>
    <div className="detail-row">
      <label>Driver Name:</label>
      <span>{viewingBus.driver?.name || 'N/A'}</span>
    </div>
    <div className="detail-row">
      <label>Driver Phone:</label>
      <span>{viewingBus.driver?.contactNumber || 'N/A'}</span> 
    </div>
    <div className="detail-row">
      <label>License Number:</label>
      <span>{viewingBus.driver?.licenseNumber || 'N/A'}</span>
    </div>
  </div>
)}

{!viewingBus.isPending && viewingBus.conductor && (
  <div className="detail-section">
    <h4>Conductor Information</h4>
    <div className="detail-row">
      <label>Conductor Name:</label>
      <span>{viewingBus.conductor?.name || 'N/A'}</span>
    </div>
    <div className="detail-row">
      <label>Conductor Phone:</label>
      <span>{viewingBus.conductor?.contactNumber || 'N/A'}</span>  
    </div>
  </div>
)}

                <div className="detail-section">
                  <h4>Owner Information</h4>
                  <div className="detail-row">
                    <label>Owner Name:</label>
                    <span><strong>{viewingBus.ownerName || 'N/A'}</strong></span>
                  </div>
                  <div className="detail-row">
                    <label>Phone Number:</label>
                    <span>{viewingBus.ownerPhone || 'N/A'}</span>
                  </div>
                  {viewingBus.ownerEmail && (
                    <div className="detail-row">
                      <label>Email:</label>
                      <span>{viewingBus.ownerEmail}</span>
                    </div>
                  )}
                  {viewingBus.ownerCompany && (
                    <div className="detail-row">
                      <label>Company:</label>
                      <span>{viewingBus.ownerCompany}</span>
                    </div>
                  )}
                  {viewingBus.ownerAddress && (
                    <div className="detail-row">
                      <label>Address:</label>
                      <span>
                        {viewingBus.ownerAddress.city}, {viewingBus.ownerAddress.state}
                        {viewingBus.ownerAddress.pincode && ` - ${viewingBus.ownerAddress.pincode}`}
                      </span>
                    </div>
                  )}
                  {!viewingBus.isPending && viewingBus.ownerCommissionRate && (
                    <div className="detail-row">
                      <label>Commission:</label>
                      <span>{viewingBus.ownerCommissionRate}% ({viewingBus.ownerCommissionType})</span>
                    </div>
                  )}
                  {!viewingBus.isPending && (
                    <>
                      <div className="detail-row">
                        <label>Verification Status:</label>
                        <span className={`status-badge ${viewingBus.ownerIsVerified ? 'active' : 'inactive'}`}>
                          {viewingBus.ownerIsVerified ? 'Verified' : 'Unverified'}
                        </span>
                      </div>
                      <div className="detail-row">
                        <label>Admin Approval:</label>
                        <span className={`status-badge ${viewingBus.ownerIsApproved ? 'active' : 'inactive'}`}>
                          {viewingBus.ownerIsApproved ? 'Approved' : 'Pending'}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="modal-close-btn secondary" onClick={closeViewModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}


      {seatStatusBus && (
        <LiveSeatStatusModal
          bus={seatStatusBus}
          token={token}
          onClose={closeSeatStatusModal}
        />
      )}


      {logModal.show && (
        <BusActivityLogModal
          busId={logModal.busId}
          busName={logModal.busName}
          busNumber={logModal.busNumber}
          onClose={() => setLogModal({ show: false, busId: null, busName: '', busNumber: '' })}
          token={token}
        />
      )}




      {showEditModal && editingPendingBus && (
        <EditBusModal
          bus={editingPendingBus}
          ownerId={editingPendingBus.owner?._id || editingPendingBus.owner}
          onClose={() => {
            setShowEditModal(false);
            setEditingPendingBus(null);
            setIsEditingPending(false);
          }}
          onUpdate={handleUpdatePendingBus}
          isPending={true}
        />
      )}
    </div>
  );
};

export default BusManagement;